import IconButton, { IconButtonProps } from "@material-ui/core/IconButton"
import SvgIcon from "@material-ui/core/SvgIcon"
import React, { CSSProperties } from "react"
import { animated } from "react-spring"
import styled from "styled-components"
import useContactInfo from "../../../hooks/useContactInfo"
import useSmallBoopDown from "../../../hooks/useSmallBoopDown"

type LinkIconProps = IconButtonProps<"a">

const Button = styled(props => <IconButton {...props} />)<LinkIconProps>`
  color: ${({ theme }) => theme.palette.tertiary.contrastText};

  :hover {
    background-color: rgba(0, 0, 0, 0.12);
  }
`

const style: CSSProperties = {
  fillRule: "evenodd",
  clipRule: "evenodd",
  strokeLinejoin: "round",
  strokeMiterlimit: 2,
}
const Icon: React.FC = ({ children }) => <SvgIcon style={style} children={children} />

const SocialButton: React.FC<LinkIconProps> = ({ href, children, ...rest }) => {
  const [style, trigger] = useSmallBoopDown()

  return (
    <animated.div style={{ ...style, display: "inline-block" }}>
      <Button
        {...rest}
        href={href}
        component="a"
        children={children}
        target="_blank"
        rel="noopener"
        edge="start"
        onMouseEnter={trigger}
      />
    </animated.div>
  )
}

const Socials: React.FC = () => {
  const { facebook, instagram, twitter, linkedin, youtube, pinterest } = {
    ...(useContactInfo() || {}),
  }

  const zeroFillStyle: CSSProperties = {
    fillRule: "nonzero",
  }

  return (
    <>
      {facebook && (
        <SocialButton href={facebook}>
          <Icon>
            <g transform="matrix(0.046875,0,0,0.046875,0,0)">
              <path
                d="M512,257.555C512,116.17 397.385,1.555 256,1.555C114.615,1.555 0,116.17 0,257.555C0,385.332 93.616,491.24 216,510.445L216,331.555L151,331.555L151,257.555L216,257.555L216,201.155C216,136.995 254.219,101.555 312.695,101.555C340.704,101.555 370,106.555 370,106.555L370,169.555L337.719,169.555C305.918,169.555 296,189.288 296,209.533L296,257.555L367,257.555L355.65,331.555L296,331.555L296,510.445C418.385,491.24 512,385.332 512,257.555Z"
                style={zeroFillStyle}
              />
            </g>
          </Icon>
        </SocialButton>
      )}
      {twitter && (
        <SocialButton href={twitter}>
          <Icon>
            <g transform="matrix(0.487329,0,0,0.487329,-1.81433,-2.51316)">
              <path
                d="M28.348,5.157C14.748,5.157 3.723,16.184 3.723,29.782C3.723,43.382 14.748,54.405 28.348,54.405C41.948,54.405 52.971,43.382 52.971,29.782C52.971,16.184 41.947,5.157 28.348,5.157ZM40.752,24.817C40.765,25.083 40.77,25.35 40.77,25.62C40.77,33.821 34.528,43.276 23.114,43.276C19.61,43.276 16.347,42.249 13.601,40.489C14.087,40.546 14.58,40.575 15.081,40.575C17.989,40.575 20.665,39.583 22.788,37.919C20.073,37.868 17.782,36.073 16.992,33.608C17.37,33.682 17.759,33.719 18.159,33.719C18.725,33.719 19.273,33.645 19.794,33.502C16.954,32.932 14.815,30.422 14.815,27.418C14.815,27.391 14.815,27.365 14.816,27.338C15.652,27.803 16.609,28.082 17.627,28.115C15.961,27 14.866,25.103 14.866,22.949C14.866,21.812 15.172,20.745 15.706,19.829C18.767,23.583 23.34,26.054 28.498,26.312C28.392,25.859 28.337,25.384 28.337,24.898C28.337,21.472 31.115,18.693 34.543,18.693C36.328,18.693 37.94,19.447 39.072,20.652C40.486,20.375 41.814,19.857 43.013,19.146C42.548,20.596 41.565,21.812 40.283,22.579C41.54,22.429 42.736,22.095 43.848,21.602C43.018,22.849 41.965,23.942 40.752,24.817Z"
                style={zeroFillStyle}
              />
            </g>
          </Icon>
        </SocialButton>
      )}
      {instagram && (
        <SocialButton href={instagram}>
          <Icon>
            <g transform="matrix(0.046875,0,0,0.046875,0,0)">
              <path d="M256,0C397.29,0 512,114.71 512,256C512,397.29 397.29,512 256,512C114.71,512 0,397.29 0,256C0,114.71 114.71,0 256,0ZM256,96C212.547,96 207.098,96.184 190.032,96.963C173.002,97.74 161.371,100.445 151.193,104.4C140.672,108.489 131.749,113.96 122.854,122.855C113.959,131.75 108.488,140.673 104.399,151.194C100.444,161.371 97.74,173.002 96.962,190.032C96.184,207.098 96,212.547 96,256C96,299.453 96.184,304.902 96.962,321.968C97.74,338.998 100.444,350.629 104.399,360.806C108.488,371.327 113.959,380.25 122.854,389.146C131.749,398.041 140.672,403.512 151.193,407.601C161.371,411.555 173.002,414.26 190.032,415.037C207.098,415.816 212.547,416 256,416C299.453,416 304.902,415.816 321.968,415.037C338.998,414.26 350.629,411.555 360.806,407.601C371.327,403.512 380.25,398.041 389.146,389.146C398.041,380.25 403.512,371.327 407.601,360.806C411.555,350.629 414.26,338.998 415.037,321.968C415.816,304.902 416,299.453 416,256C416,212.547 415.816,207.098 415.037,190.032C414.26,173.002 411.555,161.371 407.601,151.194C403.512,140.673 398.041,131.75 389.146,122.855C380.25,113.96 371.327,108.489 360.806,104.4C350.629,100.445 338.998,97.74 321.968,96.963C304.902,96.184 299.453,96 256,96ZM256,124.829C298.722,124.829 303.782,124.992 320.654,125.762C336.254,126.474 344.725,129.08 350.363,131.271C357.832,134.173 363.162,137.641 368.76,143.24C374.36,148.838 377.827,154.169 380.729,161.637C382.92,167.275 385.527,175.746 386.238,191.346C387.008,208.218 387.171,213.278 387.171,256C387.171,298.722 387.008,303.782 386.238,320.654C385.527,336.254 382.92,344.725 380.729,350.363C377.827,357.832 374.36,363.162 368.76,368.76C363.162,374.36 357.832,377.827 350.363,380.729C344.725,382.92 336.254,385.527 320.654,386.238C303.785,387.008 298.725,387.171 256,387.171C213.275,387.171 208.216,387.008 191.346,386.238C175.746,385.527 167.275,382.92 161.637,380.729C154.168,377.827 148.838,374.36 143.239,368.76C137.64,363.162 134.173,357.832 131.271,350.363C129.08,344.725 126.473,336.254 125.761,320.654C124.991,303.782 124.829,298.722 124.829,256C124.829,213.278 124.991,208.218 125.761,191.346C126.473,175.746 129.08,167.275 131.271,161.637C134.173,154.169 137.64,148.838 143.239,143.24C148.838,137.641 154.168,134.173 161.637,131.271C167.275,129.08 175.746,126.474 191.346,125.762C208.218,124.992 213.278,124.829 256,124.829ZM256,173.838C210.623,173.838 173.838,210.623 173.838,256C173.838,301.377 210.623,338.162 256,338.162C301.377,338.162 338.162,301.377 338.162,256C338.162,210.623 301.377,173.838 256,173.838ZM256,309.333C226.545,309.333 202.667,285.455 202.667,256C202.667,226.545 226.545,202.667 256,202.667C285.455,202.667 309.333,226.545 309.333,256C309.333,285.455 285.455,309.333 256,309.333ZM360.609,170.592C360.609,181.196 352.012,189.791 341.408,189.791C330.805,189.791 322.209,181.196 322.209,170.592C322.209,159.988 330.805,151.392 341.408,151.392C352.012,151.392 360.609,159.988 360.609,170.592Z" />
            </g>
          </Icon>
        </SocialButton>
      )}
      {linkedin && (
        <SocialButton href={linkedin}>
          <Icon>
            <g transform="matrix(0.75,0,0,0.75,0,0)">
              <path
                d="M16,0C7.164,0 0,7.163 0,16C0,24.836 7.164,32 16,32C24.836,32 32,24.836 32,16C32,7.163 24.836,0 16,0ZM11.844,22.277L8.58,22.277L8.58,12.457L11.844,12.457L11.844,22.277ZM10.212,11.116L10.191,11.116C9.095,11.116 8.387,10.361 8.387,9.419C8.387,8.456 9.117,7.723 10.233,7.723C11.349,7.723 12.037,8.456 12.058,9.419C12.058,10.362 11.349,11.116 10.212,11.116ZM23.613,22.277L20.349,22.277L20.349,17.023C20.349,15.703 19.877,14.803 18.695,14.803C17.794,14.803 17.257,15.41 17.021,15.997C16.935,16.207 16.913,16.5 16.913,16.793L16.913,22.277L13.65,22.277C13.65,22.277 13.693,13.379 13.65,12.457L16.914,12.457L16.914,13.848C17.347,13.179 18.123,12.226 19.856,12.226C22.003,12.226 23.613,13.63 23.613,16.646L23.613,22.277Z"
                style={zeroFillStyle}
              />
            </g>
          </Icon>
        </SocialButton>
      )}
      {youtube && (
        <SocialButton href={youtube}>
          <Icon>
            <g transform="matrix(0.046875,0,0,0.046875,0,0)">
              <path d="M256,0C397.29,0 512,114.71 512,256C512,397.29 397.29,512 256,512C114.71,512 0,397.29 0,256C0,114.71 114.71,0 256,0ZM409.315,178.978C405.635,165.209 394.793,154.368 381.025,150.688C356.067,144 256,144 256,144C256,144 155.933,144 130.975,150.688C117.21,154.368 106.365,165.209 102.685,178.978C96,203.933 96,256.002 96,256.002C96,256.002 96,308.069 102.685,333.022C106.365,346.791 117.21,357.636 130.975,361.315C155.933,368 256,368 256,368C256,368 356.067,368 381.025,361.315C394.793,357.636 405.635,346.791 409.315,333.022C416,308.069 416,256.002 416,256.002C416,256.002 416,203.933 409.315,178.978ZM223.999,304.003L223.999,208.001L307.136,256.002L223.999,304.003Z" />
            </g>
          </Icon>
        </SocialButton>
      )}
      {pinterest && (
        <SocialButton href={pinterest}>
          <Icon>
            <g id="Pinterest" transform="matrix(0.4,0,0,0.4,0,0)">
              <path d="M30,60C46.569,60 60,46.569 60,30C60,13.431 46.569,0 30,0C13.431,0 0,13.431 0,30C0,46.569 13.431,60 30,60ZM32.759,39.084C39.365,39.084 43.846,32.937 43.846,24.708C43.846,18.488 38.683,12.692 30.839,12.692C21.076,12.692 16.154,19.838 16.154,25.796C16.154,29.404 17.491,32.613 20.362,33.81C20.832,34.006 21.253,33.816 21.39,33.285C21.484,32.916 21.71,31.987 21.809,31.601C21.947,31.075 21.894,30.891 21.514,30.432C20.687,29.434 20.159,28.145 20.159,26.318C20.159,21.017 24.042,16.272 30.275,16.272C35.791,16.272 38.824,19.713 38.824,24.311C38.824,30.358 36.202,35.463 32.309,35.463C30.159,35.463 28.551,33.648 29.066,31.423C29.683,28.766 30.881,25.897 30.881,23.979C30.881,22.263 29.978,20.829 28.109,20.829C25.911,20.829 24.146,23.151 24.146,26.259C24.146,28.239 24.803,29.579 24.803,29.579C24.803,29.579 22.554,39.305 22.159,41.008C21.801,42.559 21.747,44.263 21.797,45.704C21.948,46.996 23.199,48.048 24.285,46.631C25.049,45.385 25.869,43.812 26.294,42.235C26.547,41.303 27.74,36.47 27.74,36.47C28.455,37.86 30.541,39.084 32.759,39.084Z" />
            </g>
          </Icon>
        </SocialButton>
      )}
    </>
  )
}

export default Socials
